<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import { useHead } from '@unhead/vue'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'
import { Modules } from '@/types/generated'
import LoginData = Modules.User.LoginData
import { useUserStore } from '@core/stores/user'
import { apiLogout } from '@/composables/logout'
import { productName, useBrandingStore } from '@/stores/branding'

const userStore = useUserStore()
const brandingStore = useBrandingStore()

const { t } = useI18n()

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
    public: true,
  },
})

// const title = import.meta.env.VITE_APP_RU === 'only' ? 'base_title_ru' : 'base_title'
//
// useHead({
//   title: `${t(title)} - ${t('auth.login_title')}`,
// })

const isPasswordVisible = ref<boolean>(false)
const formInProcess = ref<boolean>(false)

const route = useRoute()
const router = useRouter()
const ability = useAbility()

const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
})

const resetError = (name: string) => {
  errors.value[name] = undefined
}

const refVForm = ref<VForm>()

const form = ref<LoginData>({
  email: import.meta.env.VITE_APP_ENV === 'local' ? 'admin@test.com' : '',
  password: import.meta.env.VITE_APP_ENV === 'local' ? 'P@ssw0rd' : '',
  remember: false,
})

const login = async () => {
  try {
    const res = await $api('/auth/login', {
      method: 'POST',
      body: {
        email: form.value.email,
        password: form.value.password,
        remember: form.value.remember,
      },
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
      onResponse() {
        errors.value = {}
      },
      onResponseError({ response }) {
        // Пытаемся аккуратно достать errors из JSON-ответа; если пришёл HTML - не падаем
        const data = (response && typeof response._data === 'object') ? response._data : {}

        errors.value = (data as Record<string, any>)?.errors ?? {}
      },
    })

    // Check if 2FA is required
    if (res && typeof res === 'object' && 'two_factor' in res && (res as any).two_factor) {
      const method = (res as any).method || 'email'
      await router.push({
        name: 'two-factor-challenge',
        query: {
          method,
          to: route.query.to ? String(route.query.to) : undefined,
        },
      })
      formInProcess.value = false
      return
    }

    // Safely extract payload even if response is not the expected shape
    const payload = (res && typeof res === 'object' && 'data' in (res as any) && (res as any).data)
      ? (res as any).data
      : (typeof res === 'object' ? (res as any) : {})

    // Defensive defaults
    const abilityRules = Array.isArray(payload?.userAbilityRules) ? payload.userAbilityRules : []
    const userData = (payload && typeof payload === 'object' && 'userData' in payload) ? payload.userData : null

    const organizationSettings = (payload && typeof payload === 'object' && 'organizationSettings' in payload)
      ? payload.organizationSettings
      : null

    if (!userData) {
      throw new Error('Invalid auth response shape')
    }

    useCookie('userAbilityRules').value = abilityRules
    ability.update(abilityRules)

    useCookie('userData').value = userData
    useCookie('organizationSettings').value = organizationSettings

    userStore.hasAvatar = Boolean(userData?.avatar)
    userStore.fullName = userData?.fullName ?? ''

    const rulesCookie = useCookie('userAbilityRules', { path: '/' })
    const userCookie = useCookie('userData', { path: '/' })
    const orgCookie = useCookie('organizationSettings', { path: '/' })

    rulesCookie.value = abilityRules
    userCookie.value = userData
    orgCookie.value = organizationSettings

    // Ждём, пока появится id в куке
    const waitForUserId = () => new Promise<void>(resolve => {
      const stop = watch(
        () => userCookie.value?.id,
        id => { if (id) { stop(); resolve(); } },
        { flush: 'post' },
      )
      // Хард-таймаут, чтобы никогда не зависать
      setTimeout(() => { stop(); resolve() }, 2000)
    })

// если в payload уже есть id — ожидание не нужно
    if (userData?.id) {
      // сразу навигируемся
      await router.replace(route.query.to ? String(route.query.to) : '/')
    } else {
      // мягко подождать до 2с и навигироваться в любом случае
      await waitForUserId()
      await router.replace(route.query.to ? String(route.query.to) : '/')
    }

    await router.replace(route.query.to ? String(route.query.to) : '/')
  }
  catch (err) {
    formInProcess.value = false
    useCookie('userData').value = null
    useCookie('userAbilityRules').value = null
    ability.update([])
    await apiLogout()
    if (Object.keys(errors.value).length === 0)
      console.error(err)
  }
  finally {
    // На всякий случай выключаем лоадер в любом случае
    formInProcess.value = false
  }
}

const onSubmit = () => {
  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid) {
        formInProcess.value = true
        login()
      }
    })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <VCard
        class="auth-card pa-4"
        max-width="448"
      >
        <VCardItem class="justify-center">
          <AuthBrandHeader />
        </VCardItem>

        <VCardText
          v-if="brandingStore.organizationTitle"
          class="text-center text-body-1 pb-0"
        >
          {{ t('auth.sign_in_to_for', { app: productName, org: brandingStore.organizationTitle }) }}
        </VCardText>

        <VCardText>
          <VForm
            ref="refVForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  autofocus
                  label="Email"
                  type="email"
                  placeholder="johndoe@email.com"
                  :rules="[requiredValidator, emailValidator]"
                  :error-messages="errors?.email"
                  @update:model-value="resetError('email')"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :label="t('auth.password')"
                  autocomplete="on"
                  placeholder="············"
                  :rules="[requiredValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :error-messages="errors?.password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center justify-space-between flex-wrap mt-2 mb-4">
                  <VCheckbox
                    v-model="form.remember"
                    :label="t('auth.remember')"
                  />

                  <RouterLink
                    class="text-primary ms-2 mb-1"
                    :to="{ name: 'forgot-password' }"
                  >
                    {{ t('auth.forgot') }}
                  </RouterLink>
                </div>

                <VBtn
                  block
                  :disabled="formInProcess"
                  :loading="formInProcess"
                  type="submit"
                >
                  {{ t('auth.login') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";

.app-text-field .v-messages__message{
  position: absolute;
}
</style>
